/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {loadDevicesByIdentifiers} from '@ion/core/src/services/Connect';
import {
  BulkActionEntity,
  Device,
  GenericObject
} from '@ion/core/src/types/entities';

// the maximum number of Devices the Smart Search API returns for a single
// request, which is also the largest page size it accepts ...
export const DEVICE_SEARCH_PAGE_SIZE = 100;

export type DeviceIdentifier = string | number;

export interface FetchDevicesByIdentifiersParams {
  identifiers: DeviceIdentifier[];
  identifierType: string;
  accountId: number;
  includeSubGroups?: boolean;
};

/**
 * Splits the given identifiers into batches no larger than the Smart Search
 * page size. The API returns at most one page and does not paginate, so an
 * identifier beyond that page would otherwise match nothing at all ...
 */
export const toIdentifierBatches = (identifiers: DeviceIdentifier[] = []): DeviceIdentifier[][] => (
  identifiers.reduce((batches: DeviceIdentifier[][], identifier, index) => {
    const batch = Math.floor(index / DEVICE_SEARCH_PAGE_SIZE);
    batches[batch] = (batches[batch] || []).concat(identifier);
    return batches;
  }, [])
);

/**
 * Retrieves the Devices which match the given identifiers via the Device Smart
 * Search API, which resolves considerably faster than the Device search.
 *
 * The identifiers are requested in batches of at most the Smart Search page
 * size, each batch concurrently, and every matched Device is unwrapped from the
 * result which carries it.
 *
 * ```typescript
 *
 *  import {fetchDevicesByIdentifiers} from './deviceSearch';
 *
 *  const devices = await fetchDevicesByIdentifiers({
 *    identifiers    : ['ABC123', 'DEF456'],
 *    identifierType : 'esn',
 *    accountId
 *  });
 *
 * ```
 *
 * @param identifiers The identifier values which are to be matched.
 * @param identifierType The Device attribute against which the identifiers are
 *        matched, such as `esn`, `serialNumber`, `imei` or `id`.
 * @param accountId The account within whose hierarchy the Devices are sought.
 * @param includeSubGroups When `true` (default), Devices across the account
 *        hierarchy are searched.
 * @returns The matched Devices.
 */
export const fetchDevicesByIdentifiers = async({
  identifiers,
  identifierType,
  accountId,
  includeSubGroups = true
}: FetchDevicesByIdentifiersParams): Promise<Device[]> => {
  const responses = await Promise.all(
    toIdentifierBatches(identifiers).map(batch => loadDevicesByIdentifiers({
      accountId,
      identifierType,
      identifiers : batch,
      hierLevel   : includeSubGroups,
      pgSize      : DEVICE_SEARCH_PAGE_SIZE,
      entity      : BulkActionEntity.DEVICES,
      extension   : 'extensions'
    }))
  );
  // each Smart Search result wraps the Device it matched ...
  return responses
    .flat()
    .map((result: GenericObject) => result?.device || result)
    .filter(Boolean) as Device[];
};
